import { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import RewardEditDialog from "./RewardEditDialog";

// Mirrors REWARD_DEFAULTS in Points.js.
// Icon is intentionally excluded from Firestore — icon always comes from here.
const REWARD_DEFAULTS = [
  {
    id: "breakfast",
    name: "Complimentary Breakfast for Two",
    cost: 250,
    description: "Enjoy a full breakfast for two at our restaurant. Show the confirmation to staff at check-in.",
    icon: "🍳",
    imageUrl: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&q=80",
  },
  {
    id: "spa_credit",
    name: "Agape Spa Credit ($50 off)",
    cost: 500,
    description: "Redeem a $50 credit toward any Wellness Center service on your next visit.",
    icon: "💆",
    imageUrl: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&q=80",
  },
  {
    id: "room_upgrade",
    name: "Complimentary Room Upgrade",
    cost: 1000,
    description: "Upgrade your current booking to a higher suite tier at no extra cost, subject to availability.",
    icon: "🏨",
    imageUrl: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400&q=80",
  },
  {
    id: "wellness_session",
    name: "Complimentary Wellness Session",
    cost: 2500,
    description: "One full 60-minute wellness session of your choice at the Agape Wellness Center.",
    icon: "🌿",
    imageUrl: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&q=80",
  },
  {
    id: "free_night",
    name: "Free Night's Stay",
    cost: 5000,
    description: "One complimentary night in a select suite or villa, subject to availability.",
    icon: "🌙",
    imageUrl: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&q=80",
  },
  {
    id: "luxury_package",
    name: "Luxury Experience Package",
    cost: 10000,
    description: "The full Agape experience: one night's stay, a Wellness session, and a PB Pavilion dinner for two.",
    icon: "✨",
    imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80",
  },
];

export default function AdminRewards() {
  const [rewards, setRewards]     = useState(REWARD_DEFAULTS);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(null);
  const [saved, setSaved]         = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [infoOpen, setInfoOpen]   = useState(false);

  // Load Firestore values, merge over defaults (icons always come from REWARD_DEFAULTS)
  useEffect(() => {
    const fetchRewards = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "redeemableRewards"));
        const fsMap = {};
        snap.docs.forEach((d) => { fsMap[d.id] = d.data(); });

        setRewards(
          REWARD_DEFAULTS.map((def) => {
            const fs = fsMap[def.id];
            if (!fs) return { ...def };
            return {
              ...def,
              name:        fs.name        ?? def.name,
              description: fs.description ?? def.description,
              cost:        typeof fs.cost === "number" ? fs.cost : def.cost,
              imageUrl:    fs.imageUrl    ?? def.imageUrl,
            };
          })
        );
      } catch (err) {
        console.error("AdminRewards fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRewards();
  }, []);

  const handleChange = (id, field, value) =>
    setRewards((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const markSaved = (key) => { setSaved(key); setTimeout(() => setSaved(null), 2000); };

  const handleSave = async (reward) => {
    const costNum = parseInt(reward.cost, 10);
    if (!Number.isFinite(costNum) || costNum < 1) {
      alert("Points Required must be a positive number.");
      return;
    }
    setSaving(reward.id);
    try {
      await setDoc(
        doc(db, "redeemableRewards", reward.id),
        { name: reward.name.trim(), description: reward.description.trim(), cost: costNum },
        { merge: true }
      );
      markSaved(reward.id);
    } catch (err) {
      console.error("AdminRewards save error:", err);
      alert("Save failed. Check your admin permissions.");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <p className="admin-loading">Loading rewards…</p>;

  return (
    <div>
      {/* ── How the Rewards System Works — collapsible info panel ── */}
      <div style={{
        marginBottom: 24, border: "1px solid #e5e7eb", borderRadius: 10,
        background: "#fff", overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}>
        <button
          onClick={() => setInfoOpen((v) => !v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#0d1b4c" }}>
            ℹ️ How the Rewards System Works
          </span>
          <span style={{ fontSize: 12, color: "#6b7280", transition: "transform 0.2s", display: "inline-block", transform: infoOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </button>

        {infoOpen && (
          <div style={{ padding: "0 20px 20px", borderTop: "1px solid #f3f4f6" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, marginTop: 16 }}>
              {[
                {
                  title: "Earning Points",
                  body: "Customers earn 1 point for every $1 spent on any booking — suites & villas, wellness sessions, and PB Pavilion dining experiences. Points are awarded automatically by a Cloud Function immediately after a successful PayPal payment. The customer never needs to take any action.",
                },
                {
                  title: "Redeeming Rewards",
                  body: "Customers redeem rewards on the Rewards page (/points). When they click \"Redeem\" and confirm, a Firestore transaction deducts the reward's point cost from their current balance. Balances update in real time — the customer sees their new total the moment the transaction commits.",
                },
                {
                  title: "Points Never Expire",
                  body: "Points have no expiry date and no inactivity penalty. A customer's balance persists indefinitely as long as their account remains active.",
                },
                {
                  title: "Firestore Storage",
                  body: "Each user's balance is stored in users/{uid}/rewards/points (fields: currentPoints, totalEarned, rewardsRedeemed). Every earn and redeem action writes a record to users/{uid}/pointsHistory, which powers the transaction timeline on the Rewards page.",
                },
                {
                  title: "Transaction History",
                  body: "The Points History section on the Rewards page shows all earned and redeemed transactions in reverse-chronological order. Cart checkouts show a collapsible breakdown of each individual item and its point value.",
                },
                {
                  title: "Editing Rewards",
                  body: "Use the cards below to update any reward's name, description, and point cost. Click the pencil icon on a card's thumbnail to open the full editor including image upload. Changes are saved to Firestore and reflected immediately on the customer-facing Rewards page — no redeploy needed.",
                },
              ].map(({ title, body }) => (
                <div key={title} style={{
                  background: "#f9fafb", borderRadius: 8, padding: "14px 16px",
                  border: "1px solid #e5e7eb",
                }}>
                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#0d1b4c", textTransform: "uppercase", letterSpacing: "0.6px" }}>{title}</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.65 }}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, maxWidth: 600 }}>
        Edit the name, description, and points required for each redeemable reward.
        Click the pencil icon on a reward's photo to open the full editor including image upload.
        Changes are reflected immediately on the customer-facing Rewards page.
      </p>

      <div className="content-editor-grid">
        {rewards.map((reward) => (
          <div key={reward.id} className="content-card">

            {/* Thumbnail with pencil overlay */}
            <div style={{ position: "relative", marginBottom: 12 }}>
              <img
                src={reward.imageUrl}
                alt={reward.name}
                style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, display: "block" }}
              />
              <button
                onClick={() => setEditingReward(reward)}
                title="Edit reward"
                style={{
                  position: "absolute", bottom: 8, right: 8,
                  background: "rgba(255,255,255,0.92)", border: "none",
                  borderRadius: "50%", width: 30, height: 30,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)", fontSize: 13,
                }}
              >
                ✏️
              </button>
            </div>

            <p
              className="content-card-label"
              style={{ fontSize: 13, fontWeight: 700, color: "#0d1b4c", marginBottom: 4 }}
            >
              {reward.id}
            </p>

            <p className="content-card-label">Name</p>
            <input
              className="content-input"
              value={reward.name}
              onChange={(e) => handleChange(reward.id, "name", e.target.value)}
            />

            <p className="content-card-label">Description</p>
            <textarea
              className="content-textarea"
              value={reward.description}
              onChange={(e) => handleChange(reward.id, "description", e.target.value)}
              rows={3}
            />

            <p className="content-card-label">Points Required</p>
            <input
              className="content-input"
              type="number"
              min={1}
              value={reward.cost}
              onChange={(e) => handleChange(reward.id, "cost", e.target.value)}
            />

            <button
              className={`content-save-btn ${saved === reward.id ? "saved" : ""}`}
              onClick={() => handleSave(reward)}
              disabled={saving === reward.id}
            >
              {saving === reward.id ? "Saving…" : saved === reward.id ? "✓ Saved" : "Save Changes"}
            </button>
          </div>
        ))}
      </div>

      {/* Shared reward edit dialog — handles image upload + all text fields */}
      <RewardEditDialog
        reward={editingReward}
        onClose={() => setEditingReward(null)}
        onSaved={(updates) => {
          setRewards((prev) =>
            prev.map((r) => r.id === updates.id ? { ...r, ...updates } : r)
          );
        }}
      />
    </div>
  );
}
